import { IAuthenticationPort } from '../../ports/AuthenticationPort';
import { IPermission } from '../../modules/onboarding/onboardingModels';
import { v4 as uuid4 } from 'uuid';
import { User } from 'oidc-client';

let authPort: IAuthenticationPort | null = null;
let globalPermissions: IPermission[] = [];
let isLicensePrivilegedUser: boolean = false;
let ldFlags: Record<string, any> = {};

export function initializeAuthService(port: IAuthenticationPort): void {
  authPort = port;
}

function getPort(): IAuthenticationPort {
  if (!authPort) {
    throw new Error(
      'Auth singleton not initialized. Call initializeAuthService() at app startup.'
    );
  }
  return authPort;
}

export const authService = {
  get baseURL(): string {
    return window.ENV?.BASE_URL || '';
  },

  // Alias for backward compatibility
  get BASE_URL(): string {
    return this.baseURL;
  },
  
  get offering(): string {
    return window.ENV?.OFFERING || '';
  },

  // Alias for backward compatibility
  get OFFERING(): string {
    return this.offering;
  },

  get globalPermissions(): IPermission[] {
    return globalPermissions;
  },

  get isLicensePrivilegedUser(): boolean {
    return isLicensePrivilegedUser;
  },

  get flags(): Record<string, any> {
    return ldFlags;
  },

  set flags(value: Record<string, any>) {
    ldFlags = value;
  },

  sessionTimer: 3600,

  lastActiveTime: new Date().getTime(),

  resetSessionTimer(): void {
    this.sessionTimer = 3600;
    this.lastActiveTime = new Date().getTime();
  },
  
  getUser: async () => {
    return getPort().getCurrentUser();
  },
  
  getAccessToken: async () => {
    const token = await getPort().getAccessToken();
    return token.value;
  },
  
  getTokenDetails: async () => {
    const token = await getPort().getAccessToken();
    const user = await getPort().getCurrentUser();
    return {
      access_token: token.value,
      id_token: user?.metadata?.idToken as string | undefined,
      expires_at: typeof token.expiresAt === 'string' 
        ? new Date(token.expiresAt).getTime() / 1000 
        : token.expiresAt.getTime() / 1000
    };
  },

  renewToken: async (): Promise<User> => {
    return await getPort().signinSilent();
  },

  addUserLoaded: (callback: (user: User) => void): void => {
    return getPort().addUserLoaded(callback);
  },

  removeUserLoaded: (callback: (user: User) => void): void => {
    return getPort().removeUserLoaded(callback);
  },
  
  authFetch: async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const token = await getPort().getAccessToken();
    
    const authHeaders = new Headers(init?.headers);
    authHeaders.set('Authorization', `Bearer ${token.value}`);
    authHeaders.set('X-SVCorId', uuid4());
    
    return fetch(input, {
      ...init,
      headers: authHeaders
    });
  },

  login: async (): Promise<void> => {
    return getPort().signIn();
  },

  signOut: async (): Promise<void> => {
    const user = await getPort().getCurrentUser();
    if (user) {
      sessionStorage.setItem('POST_SIGNIN_LOCATION', window.location.href);
      sessionStorage.removeItem(`globalPermissions:${user.email}`);
      sessionStorage.removeItem('NEW_SUBSCRIPTION_NOTIFICATION');
    }
    return getPort().signOut();
  },

  setGlobalPermissionsFromCached: (userName: string | undefined): boolean => {
    if (userName) {
      const cachedGlobalPerms = sessionStorage.getItem(`globalPermissions:${userName}`);
      globalPermissions = JSON.parse(cachedGlobalPerms || '[]');
      return !!cachedGlobalPerms;
    }
    return false;
  },

  setGlobalPermissions: async (userName: string | undefined): Promise<void> => {
    const response = await authService.authFetch(
      `${authService.baseURL}/api/v1/users/me/privileges?scopes=UserManagement&scopes=OrgManagement&scopes=SubscriptionManagement&scopes=AssetModeling&scopes=ServiceAccountManagement&scopes=LicenseManagement`
    );
    if (response.ok) {
      const perms = await response.json();
      globalPermissions = perms;
      if (userName) {
        sessionStorage.setItem(`globalPermissions:${userName}`, JSON.stringify(perms));
      }
    } else {
      throw new Error(response.statusText);
    }
  },

  checkGlobalPermissionsCached: (userName: string | undefined): boolean => {
    return !!sessionStorage.getItem(`globalPermissions:${userName}`);
  },

  hasGlobalPermission: (queryPermission: string): boolean => {
    return !!globalPermissions.find(permission => permission.name === queryPermission);
  },

  fetchNodePermissions: (orgId: string, nodeId: string): Promise<Response> => {
    return authService.authFetch(
      `${authService.baseURL}/api/v1/users/orgs/${orgId}/nodes/${nodeId}/privileges`
    );
  },

  getOrgPermFetchURL: (orgId?: string): string => {
    return `${authService.baseURL}/api/v1/users/orgs/${orgId}/nodes/${orgId}/privileges?scopes=OrgManagement`;
  },

  getOrgExportPermFetchURL: (orgId?: string): string => {
    return `${authService.baseURL}/api/v1/users/orgs/${orgId}/can-update-orghierarchy`;
  },

  getSitePermFetchURL: (orgId: string, siteId: string): string => {
    return `${authService.baseURL}/api/v1/users/orgs/${orgId}/nodes/${siteId}/privileges?scopes=ModelManagement`;
  },

  getUserManagementPermFetchURL: (orgId?: string): string => {
    return `${authService.baseURL}/api/v1/users/me/orgs/${orgId}/privileges?scopes=UserManagement`;
  },

  getSubscriptionPermFetchURL: (orgId?: string): string => {
    return `${authService.baseURL}/api/v1/users/orgs/${orgId}/nodes/${orgId}/privileges?scopes=SubscriptionManagement`;
  },

  hasPermission: (permissions: IPermission[] | null, query: string): boolean => {
    const checkPerm = (perm: IPermission) => perm.name === query;
    return (
      (permissions ? !!permissions.find(checkPerm) : false) ||
      !!globalPermissions.find(checkPerm)
    );
  },

  fetchUserAllPrivileges: async (scope: string): Promise<IPermission[]> => {
    const apiUrl = `${authService.baseURL}/api/v1/users/me/user-all-privileges?scopes=${scope}`;
    try {
      const response = await authService.authFetch(apiUrl, { method: 'GET' });
      if (response.status === 200) {
        const privileges = await response.json();
        return privileges as IPermission[];
      } else {
        return [];
      }
    } catch (error) {
      console.error(`${scope} user-all-privileges: Error`, error);
      return [];
    }
  },

  checkLicensesPrivilege: async (userEmail: string | undefined, userId: string | undefined, siOrgId: string): Promise<boolean> => {
    if ((!userEmail && !userId) || !siOrgId) {
      isLicensePrivilegedUser = false;
      return false;
    }

    const apiUrl = `${authService.baseURL}/api/v1/orgs/${siOrgId}/licenses/privileged-users`;
    try {
      const response = await authService.authFetch(apiUrl, { method: 'GET' });
      if (response.status === 200) {
        const result = await response.json();
        const users: string[] = result.data || [];
        isLicensePrivilegedUser = users.some(
          (entry: string) => {
            const entryLower = entry.toLowerCase();
            return (userEmail && entryLower === userEmail.toLowerCase()) ||
                   (userId && entryLower === userId.toLowerCase());
          }
        );
        return isLicensePrivilegedUser;
      }
      isLicensePrivilegedUser = false;
      return false;
    } catch (error) {
      console.error('checkLicensesPrivilege: Error', error);
      isLicensePrivilegedUser = false;
      return false;
    }
  }
};
